import { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { useLanguageContext } from '@/i18n/language-context';
import { publicRoutes } from '@/constant/auth-public-routes';

/**
 * LanguageSelector Component
 *
 * A dropdown menu component that allows users to select their preferred language
 * from a list of available languages fetched from the API.
 *
 * @example
 * // Basic usage in a navigation bar
 * <nav className="flex items-center justify-between">
 *   <Logo />
 *   <div className="flex items-center gap-4">
 *     <LanguageSelector />
 *     <UserMenu />
 *   </div>
 * </nav>
 */

interface LanguageSelectorProps {
  variant?: 'default' | 'landing';
}

export const LanguageSelector = ({ variant = 'default' }: LanguageSelectorProps) => {
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { currentLanguage, setLanguage, availableLanguages, isLoading } = useLanguageContext();
  const location = useLocation();

  useEffect(() => {
    if (!isLoading && availableLanguages?.length > 0) {
      const currentLanguageExists = availableLanguages.some(
        (lang) => lang.languageCode === currentLanguage
      );

      if (!currentLanguageExists) {
        const defaultLanguage = availableLanguages.find((lang) => lang.isDefault);
        if (defaultLanguage) {
          setLanguage(defaultLanguage.languageCode);
        }
      }
    }
  }, [availableLanguages, currentLanguage, isLoading, setLanguage]);

  const isAuthLayout = publicRoutes.some((path) => location.pathname.startsWith(path));

  const changeLanguage = async (newLanguageCode: string) => {
    await setLanguage(newLanguageCode);
  };

  return (
    <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
      <DropdownMenuTrigger asChild className="cursor-pointer">
        <div
          className={cn(
            'flex items-center gap-1 h-[34px] px-2 rounded-[4px]',
            variant === 'landing' ? '' : 'hover:bg-surface'
          )}
        >
          {variant === 'landing' && <Globe className="h-4 w-4 text-[#1B2021]" />}
          <span
            className={cn(
              'text-sm',
              variant === 'landing'
                ? 'font-medium text-[#1B2021]'
                : 'font-semibold text-medium-emphasis'
            )}
          >
            {availableLanguages?.find((lang) => lang.languageCode === currentLanguage)
              ?.languageName || currentLanguage}
          </span>
          {variant === 'default' &&
            (isDropdownOpen ? (
              <ChevronUp className="h-4 w-4 text-medium-emphasis" />
            ) : (
              <ChevronDown className="h-4 w-4 text-medium-emphasis" />
            ))}
        </div>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end">
        {isLoading && (
          <DropdownMenuItem disabled>
            {isAuthLayout ? t('LOADING_LANGUAGES') + '...' : <Skeleton className="h-4 w-24" />}
          </DropdownMenuItem>
        )}
        {!isLoading &&
          (availableLanguages && availableLanguages.length > 0 ? (
            availableLanguages.map((lang, i) => (
              <div key={lang.itemId}>
                <DropdownMenuItem
                  className={cn({
                    'font-bold cursor-pointer': lang.languageCode === currentLanguage,
                  })}
                  onClick={() => changeLanguage(lang.languageCode)}
                >
                  {lang.languageName} {lang.isDefault && '(Default)'}
                </DropdownMenuItem>
                {i !== availableLanguages.length - 1 && <DropdownMenuSeparator />}
              </div>
            ))
          ) : (
            <DropdownMenuItem disabled>{t('NO_LANGUAGES_AVAILABLE')}</DropdownMenuItem>
          ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
